import { Injectable } from '@angular/core';
import { ApplicationHttpClient } from '@app/helpers/custom-http-client';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  HttpResult,
  HttpResultWithPagination,
} from '@app/shared/models/http-result';
import { FilterRequest } from '@app/shared/models/filter';
import { FacebookPagesStatsModel } from './models/facebook-pages-stats.model';
import { Pagination, PagingRequest } from '@app/shared/models/paging.class';
import { FacebookPageModel } from './models/facebook-page.model';
import { FacebookCategoryModel } from './models/facebook-categories.model';
import { GeoKLocation } from '@app/shared/models/geo-k-location';
import { HttpParams } from '@angular/common/http';
import { FacebookPageSummaryModel } from './models/facebook-page-summary.model';

@Injectable({
  providedIn: 'root',
})
export class FacebookPagesService {
  constructor(private http: ApplicationHttpClient) {}
  getFacebookPagesStats(
    network: string,
    filters?: FilterRequest[]
  ): Observable<FacebookPagesStatsModel> {
    let url = `/users/me/third-party-locations/stats?network=${network}`;

    if (!!filters && filters.length !== 0) {
      filters = filters.filter((filter) => filter.property !== 'tagId');

      if (!!filters && filters.length != 0) {
        url += `&filter=${JSON.stringify(filters)}`;
      }
    }
    return this.http.get<HttpResult>(url).pipe(map((it) => it.data));
  }

  getActivatedFacebookPages(
    network: string,
    pagingRequest: PagingRequest,
    filters?: FilterRequest[]
  ): Observable<{
    items: FacebookPageModel[];
    pagination: Pagination;
  }> {
    const url = `/integrations/social-media/third-party-locations`;
    let query = `?network=${network}&page=${pagingRequest.page}&limit=${pagingRequest.limit}`;

    if (!!filters && filters.length !== 0) {
      filters = filters.filter((filter) => filter.property !== 'tagId');

      if (!!filters && filters.length != 0) {
        query += `&filter=${JSON.stringify(filters)}`;
      }
    }

    return this.http.get<HttpResultWithPagination>(url + query).pipe(
      map((it) => {
        return {
          items: it.data,
          pagination: {
            pageIndex: it.page,
            totalCount: it.totalCount,
          },
        };
      })
    );
  }

  getFacebookPageById(locationId: string): Observable<FacebookPageModel> {
    const url = `/integrations/social-media/third-party-locations/${locationId}`;
    return this.http.get<HttpResult>(url).pipe(map((it) => it.data));
  }

  unlinkFacebookPageFromGbp(thirdPartyLocationId: string): Observable<any> {
    const url = `/integrations/social-media/third-party-locations/${thirdPartyLocationId}/unlink`;
    return this.http.delete(url);
  }

  linkFacebookPageToGbp(
    brandId: string,
    location: {
      links: { thirdPartyLocationId: string; gbpLocationId: string }[];
    }
  ): Observable<any> {
    const url = `/integrations/social-media/brand/${brandId}/link-locations`;
    return this.http.post(url, location);
  }

  deleteFacebookPage(locationId: string): Observable<void> {
    const url = `/integrations/social-media/third-party-locations/${locationId}`;
    return this.http.delete(url);
  }

  updateFacebookPage(
    id: string,
    property: NonNullable<unknown>
  ): Observable<FacebookPageModel> {
    const url = `/integrations/social-media/third-party-locations/${id}`;
    return this.http.put<HttpResult>(url, property).pipe(map((it) => it.data));
  }

  getFacebookPageAiDescription(
    thirdPartyLocationId: string
  ): Observable<string> {
    const url = `/integrations/social-media/third-party-locations/${thirdPartyLocationId}/ai-description`;
    return this.http.get<HttpResult>(url).pipe(map((it) => it.data));
  }

  getFacebookCategories(
    facebookPageId: string,
    query: string,
    limit: 20
  ): Observable<FacebookCategoryModel[]> {
    const url = `/integrations/social-media/third-party-locations/${facebookPageId}/available-categories?search=${query}&limit=${limit}`;
    return this.http.get<HttpResult>(url).pipe(map((it) => it.data));
  }

  getUnlinkedGbpLocationsForSocialMedia(
    thirdPartyLocationId: string,
    page: { page: number; limit: number }
  ): Observable<{
    items: GeoKLocation[];
    pagination: { pageIndex: number; totalCount: number };
  }> {
    const url = `/integrations/social-media/third-party-locations/${thirdPartyLocationId}/unlinked-gbp-locations?page=${page.page}&limit=${page.limit}`;
    return this.http.get<HttpResultWithPagination>(url).pipe(
      map((it) => {
        return {
          items: it.data,
          pagination: {
            pageIndex: it.page,
            totalCount: it.totalCount,
          },
        };
      })
    );
  }

  getBrandFacebookPages(
    network: string,
    brandId: string
  ): Observable<FacebookPageSummaryModel[]> {
    const url = `/integrations/social-media/brand/${brandId}`;
    return this.http
      .get<HttpResult>(url, {
        params: new HttpParams().set('channel', network),
      })
      .pipe(map((it) => it.data));
  }

    manageFacebookPages(
    brandId: string,
    integrationIds: string[]
  ): Observable<any> {
    const url = `/integrations/social-media/manage-locations/${brandId}`;
    return this.http.post(url, { integrationIds });
  }
}
